import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { initialServices } from '../../data/initialData';

const iconOptions = ['🧠', '📝', '🌐', '💾', '🎤', '⚙️', '🔬', '📊', '🏗️', '🚀', '🔒', '📡', '🔄', '🎯', '💻', '🤖', '📚', '🔍'];

const ServicesManager = () => {
  const [services, setServices] = useLocalStorage('services', initialServices);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', icon: '🧠' });

  const resetForm = () => {
    setFormData({ title: '', description: '', icon: '🧠' });
    setIsAdding(false);
    setEditingIndex(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newList = [...services];
    if (editingIndex !== null) {
      newList[editingIndex] = formData;
    } else {
      newList.push(formData);
    }
    setServices(newList);
    resetForm();
  };

  const handleEdit = (index) => {
    setFormData(services[index]);
    setEditingIndex(index);
    setIsAdding(true);
  };

  const handleDelete = (index) => {
    if (window.confirm('Delete this service?')) {
      setServices(services.filter((_, i) => i !== index));
    }
  };

  const handleMove = (index, direction) => {
    const newList = [...services];
    const target = index + direction;
    if (target < 0 || target >= newList.length) return;
    [newList[index], newList[target]] = [newList[target], newList[index]];
    setServices(newList);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Services</h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsAdding(true)}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg"
        >
          Add Service
        </motion.button>
      </div>

      {isAdding && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-700/50 rounded-lg p-6 mb-6 border border-slate-600"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            {editingIndex !== null ? 'Edit Service' : 'Add Service'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <input type="text" value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 bg-slate-600/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="w-full px-4 py-2 bg-slate-600/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Icon</label>
              <div className="flex flex-wrap gap-2">
                {iconOptions.map(icon => (
                  <motion.button key={icon} type="button"
                    whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setFormData({...formData, icon})}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg border-2 transition-all ${
                      formData.icon === icon ? 'border-cyan-400 bg-cyan-500/20' : 'border-slate-600 bg-slate-700/50 hover:border-slate-400'
                    }`}>
                    {icon}
                  </motion.button>
                ))}
              </div>
            </div>
            <div className="flex space-x-3">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition-colors">
                {editingIndex !== null ? 'Update' : 'Add'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={resetForm}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors">Cancel</motion.button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-3">
        {services.map((item, index) => (
          <motion.div key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-slate-700/30 rounded-lg p-4 border border-slate-600"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-lg">{item.icon}</span>
                  <h3 className="text-base font-semibold text-white truncate">{item.title}</h3>
                </div>
                <p className="text-sm text-gray-400 line-clamp-2 ml-9">{item.description}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={() => handleMove(index, -1)} disabled={index === 0}
                  className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7"/></svg>
                </motion.button>
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={() => handleMove(index, 1)} disabled={index === services.length - 1}
                  className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </motion.button>
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={() => handleEdit(index)}
                  className="p-1.5 text-blue-400 hover:text-blue-300 transition-colors">✏️</motion.button>
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={() => handleDelete(index)}
                  className="p-1.5 text-red-400 hover:text-red-300 transition-colors">🗑️</motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ServicesManager;
